/**
 * Front-desk operations contracts (WP1-FRONTDESK, module id `frontdesk`,
 * routes under `/v1/frontdesk`). Library: checkout.md, eod-closeout.md,
 * tasks-notes.md, documents.md.
 *
 * Money is integer cents everywhere. Dates are office-local `YYYY-MM-DD`;
 * instants are ISO 8601 strings with offset.
 */
import { z } from 'zod';

const Id = z.uuid();
const LocalDate = z.iso.date();
const Instant = z.string().min(1);
const Cents = z.number().int();

export const FRONTDESK_PERMISSIONS = {
  /** Open and run the checkout sheet; status moves. */
  checkout: 'schedule.edit',
  /** Move a checked-in visit to Checkout (opening the sheet); either this or checkout (NEW-WF-06). */
  checkoutOpen: 'schedule.flow',
  /** Post a payment at checkout or at the drawer. */
  postPayment: 'ledger.post_payment',
  /** Post procedures complete from checkout. */
  postProcedure: 'chart.post_complete',
  /** View money (balances, the day sheet, deposit slip reprints). */
  moneyView: 'money.view',
  /** Close, reopen and approve variances; post into a closed (locked) day. */
  closeDay: 'money.close_day',
  /** Read task lists and the contact log. */
  tasksView: 'task.view',
  /** Create and work tasks, routines and contact logs. */
  tasksEdit: 'task.create',
  /** See documents. */
  documentsView: 'documents.view',
  /** Upload, move, rotate, sign, delete and restore documents. */
  documentsEdit: 'documents.upload',
  /** See (and therefore change) restricted documents. */
  documentsRestricted: 'audit.view',
  /** Legal holds and retention runs. */
  documentsAdmin: 'documents.legal_hold',
  /** Set up the office's document types (Document Center). */
  documentTypesManage: 'settings.manage',
} as const;

/* ------------------------------------------------------------------------ */
/* Office mode                                                               */
/* ------------------------------------------------------------------------ */

export const GateSchema = z.object({
  enabled: z.boolean(),
  reason: z.string().nullable(),
  /** Why a disabled control is off: the user's permission, or what the office's PMS accepts. */
  cause: z.enum(['permission', 'pms']).nullable().optional(),
});
export type GateView = z.infer<typeof GateSchema>;

export const OfficeModeSchema = z.object({
  locationId: Id,
  name: z.string(),
  shortName: z.string(),
  timezone: z.string(),
  /** `native` when no PMS is linked; otherwise the PMS label. */
  pms: z.string().nullable(),
  stage: z.string(),
  /** Who owns money at this office. */
  ledgerOwner: z.enum(['velora', 'pms']),
});
export type OfficeMode = z.infer<typeof OfficeModeSchema>;

/* ------------------------------------------------------------------------ */
/* Checkout                                                                  */
/* ------------------------------------------------------------------------ */

export const FLOW_STATUSES = [
  'scheduled',
  'late',
  'here',
  'ready',
  'chair',
  'checkout',
  'complete',
] as const;
export const FlowStatusSchema = z.enum(FLOW_STATUSES);

export const CheckoutQueueItemSchema = z.object({
  appointmentId: Id,
  locationId: Id,
  officeShort: z.string(),
  patientId: Id,
  patientName: z.string(),
  patientAge: z.number().int().nullable(),
  householdId: Id.nullable(),
  startsAt: Instant,
  lengthMin: z.number().int(),
  providerAbbr: z.string().nullable(),
  operatory: z.string().nullable(),
  flowStatus: FlowStatusSchema,
  /** Minutes since the visit entered its current status. */
  minutesInStatus: z.number().int().nullable(),
  unpostedCount: z.number().int(),
  dueTodayCents: Cents,
  /** The first open checklist item ("Needs payment", "No next visit"). */
  firstOpenItem: z.string().nullable(),
  finished: z.boolean(),
});
export type CheckoutQueueItem = z.infer<typeof CheckoutQueueItemSchema>;

/** What the office's PMS feed carries for a checkout data class (S3, S16, LED-06). */
export const FeedStateSchema = z.object({
  /** `velora`: Velora's own data (no PMS, or Velora owns the class). */
  state: z.enum(['velora', 'synced', 'not_synced']),
  reason: z.string().nullable(),
});
export type FeedStateView = z.infer<typeof FeedStateSchema>;

export const CheckoutQueueSchema = z.object({
  date: LocalDate,
  items: z.array(CheckoutQueueItemSchema),
  /**
   * Per office in scope: whether patient flow (arrived, seated, checkout)
   * comes from the PMS. When it is not synced the queue fills only with
   * visits staff move in Velora, so the empty state says so (S3).
   */
  offices: z
    .array(
      z.object({
        locationId: Id,
        shortName: z.string(),
        pms: z.string().nullable(),
        flow: FeedStateSchema,
      }),
    )
    .optional(),
});
export type CheckoutQueue = z.infer<typeof CheckoutQueueSchema>;

export const ScrubStateSchema = z.object({
  state: z.enum(['ready', 'blocked', 'none']),
  message: z.string(),
});

export const CheckoutProcedureSchema = z.object({
  id: Id,
  appointmentId: Id.nullable(),
  patientId: Id,
  code: z.string(),
  description: z.string(),
  tooth: z.string().nullable(),
  surfaces: z.string().nullable(),
  providerAbbr: z.string().nullable(),
  /** OD ProcStat. */
  status: z.number().int(),
  posted: z.boolean(),
  feeCents: Cents,
  insEstCents: Cents,
  writeoffEstCents: Cents,
  patientPortionCents: Cents,
  dxIcd10: z.array(z.string()),
  scrub: ScrubStateSchema,
  /** A write-back command is pending for this row. */
  pendingCommand: z.string().nullable(),
  /**
   * How the posted row stands against the PMS: `pms` (from the PMS),
   * `velora` (Velora is the system of record), `queued` (command pending),
   * `written` (command applied), `failed` (the command failed: not in the
   * PMS), `draft` (posted in Velora only; complete it in the PMS).
   */
  writeState: z
    .enum(['pms', 'velora', 'queued', 'written', 'failed', 'draft'])
    .nullable()
    .optional(),
  writeNote: z.string().nullable().optional(),
  /** The retail sale this line is (W15): a product sold at checkout, removable before posting. */
  retailSaleId: Id.nullable().optional(),
  /**
   * A failed write-back (CO-12): whether Velora may send it again (only when
   * the PMS reported it never saved it, and the office's write path still
   * allows the command), and where to find the row in the PMS by hand.
   */
  failedWrite: z
    .object({
      commandId: Id,
      status: z.string(),
      canRetry: z.boolean(),
      retryReason: z.string().nullable(),
      /** "Open in <PMS>": the PMS, the patient's PMS id and what to open. */
      pms: z.string(),
      patientPmsId: z.string().nullable(),
      openHint: z.string(),
    })
    .nullable()
    .optional(),
});
export type CheckoutProcedure = z.infer<typeof CheckoutProcedureSchema>;

export const CheckoutPaymentSchema = z.object({
  id: Id,
  amountCents: Cents,
  method: z.string(),
  paidAt: Instant,
  reference: z.string().nullable(),
  checkNumber: z.string().nullable(),
  /** Write-back state when the PMS owns money. */
  writeBack: z.string().nullable(),
  /** `native` Velora ledger, `command` queued to the PMS, `manual` a "Post in <PMS>" task. */
  writeBackMode: z.enum(['native', 'command', 'manual']).nullable().optional(),
  /** The note staff type into the PMS so sync can match the payment (VEL-XXXXXXXX). */
  pmsReference: z.string().nullable().optional(),
  taskId: Id.nullable().optional(),
  /** Reversed from Payments > Recent payments. */
  reversed: z.boolean().optional(),
});

export const ChecklistItemSchema = z.object({
  key: z.enum(['procedures', 'phone', 'email', 'payment', 'next_visit', 'forms']),
  label: z.string(),
  done: z.boolean(),
  required: z.boolean(),
  detail: z.string(),
  skipReasons: z.array(z.string()),
});
export type ChecklistItemView = z.infer<typeof ChecklistItemSchema>;

export const SlotSchema = z.object({
  date: LocalDate,
  minute: z.number().int(),
  startsAt: Instant,
  preferred: z.boolean(),
  operatoryId: Id,
  operatory: z.string(),
  providerId: Id,
  providerAbbr: z.string(),
  lengthMin: z.number().int(),
});
export type SlotView = z.infer<typeof SlotSchema>;

export const CheckoutSectionSchema = z.object({
  appointmentId: Id,
  patientId: Id,
  patientName: z.string(),
  patientAge: z.number().int().nullable(),
  relationship: z.string().nullable(),
  startsAt: Instant,
  lengthMin: z.number().int(),
  providerAbbr: z.string().nullable(),
  operatory: z.string().nullable(),
  flowStatus: FlowStatusSchema,
  timeline: z.object({
    arrivedAt: Instant.nullable(),
    seatedAt: Instant.nullable(),
    checkoutAt: Instant.nullable(),
  }),
  procedures: z.array(CheckoutProcedureSchema),
  dueTodayCents: Cents,
  recall: z
    .object({
      dueDate: LocalDate.nullable(),
      typeName: z.string(),
      intervalMonths: z.number().int().nullable(),
    })
    .nullable(),
  nextVisit: z
    .object({
      /**
       * `pending_pms`: picked at checkout while appointment write-back is off;
       * a "Do in <PMS>" task carries the details and the visit counts as
       * reappointed ("Booked, pending Dentrix").
       */
      kind: z.enum(['booked', 'patient_will_call', 'pending_pms']),
      startsAt: Instant.nullable(),
      reason: z.string().nullable(),
      /** The "Do in <PMS>" task for a `pending_pms` booking. */
      taskId: Id.nullable().optional(),
      /** "RDH1 · HYG1 · 60 min · Perio maintenance" for a `pending_pms` booking. */
      detail: z.string().nullable().optional(),
      /**
       * The booking's write-back to the PMS (WS-W-7): `queued` while the
       * create is pending, `failed` when the PMS refused it (the visit was
       * taken back off the book; book another time).
       */
      writeBack: z
        .object({
          status: z.enum(['not_required', 'queued', 'written', 'failed', 'task']),
          message: z.string().nullable(),
        })
        .nullable()
        .optional(),
    })
    .nullable(),
  slots: z.array(SlotSchema),
  checklist: z.array(ChecklistItemSchema),
  contact: z.object({ mobile: z.string().nullable(), email: z.string().nullable() }),
  finished: z.boolean(),
});
export type CheckoutSection = z.infer<typeof CheckoutSectionSchema>;

export const CheckoutSheetSchema = z.object({
  office: OfficeModeSchema,
  date: LocalDate,
  primaryAppointmentId: Id,
  guarantorId: Id,
  guarantorName: z.string(),
  sections: z.array(CheckoutSectionSchema),
  /** Household members with a visit today in chair or checkout, not yet added. */
  householdCandidates: z.array(
    z.object({
      appointmentId: Id,
      patientName: z.string(),
      patientAge: z.number().int().nullable(),
      flowStatus: FlowStatusSchema,
    }),
  ),
  money: z.object({
    feesCents: Cents,
    insEstCents: Cents,
    dueTodayCents: Cents,
    paidTodayCents: Cents,
    /** Prior balance still owed: before today, less any of today's payments beyond today's portion. */
    priorBalanceCents: Cents,
    /** Prior balance as the day started (before today's payments). */
    priorBalanceStartCents: Cents.optional(),
    /** Unapplied family credit (prepayments); never applied by default. */
    creditCents: Cents,
    creditDecision: z.enum(['applied', 'kept']).nullable(),
    remainingTodayCents: Cents,
    suggestions: z.array(z.object({ key: z.string(), label: z.string(), cents: Cents })),
    payments: z.array(CheckoutPaymentSchema),
    /** What the family owes now on this sheet (today remaining + prior balance), WM-2. */
    owedNowCents: Cents.optional(),
    /** The largest payment the API accepts here: owed now + the office overpayment limit. */
    maxPaymentCents: Cents.optional(),
    /** Office overpayment allowance (0 = no overpayments at checkout). */
    overpayMaxCents: Cents.optional(),
    /** How a payment is recorded here and what reaches the PMS (WM-1, LED-06). */
    writePath: z
      .object({
        mode: z.enum(['native', 'command', 'manual']),
        reason: z.string().nullable(),
        sentence: z.string(),
      })
      .optional(),
    /** The collect button's label ("Record receipt in Velora (PMS ledger not synced)"). */
    collectLabel: z.string().optional(),
  }),
  /** PMS feeds this sheet depends on (S3, S16, LED-06). */
  feeds: z
    .object({ flow: FeedStateSchema, ledger: FeedStateSchema, apptProcedureLink: FeedStateSchema })
    .optional(),
  insurance: z.object({
    carrier: z.string().nullable(),
    plan: z.string().nullable(),
    blocked: z.array(z.string()),
    claimNote: z.string(),
  }),
  gates: z.object({
    postProcedure: GateSchema,
    payment: GateSchema,
    book: GateSchema,
    complete: GateSchema,
    textReceipt: GateSchema,
    textToPay: GateSchema,
    /** Copy a pay link to paste elsewhere (WM-16), when texting is off. */
    copyPayLink: GateSchema.optional(),
    cardPresent: GateSchema,
  }),
  /**
   * "Charge on terminal" (ADR-0015): the office's integrated readers (Sprout
   * PAX, Square or Stripe) and the card type the payment posts under when
   * the reader's brand matches none. Null when no reader is set up.
   */
  terminal: z
    .object({
      readers: z.array(z.object({ id: z.uuid(), label: z.string() })),
      paymentTypeCode: z.string(),
      processorLabel: z.string(),
      live: z.boolean(),
    })
    .nullable()
    .optional(),
  /**
   * Where a next-visit pick lands: Velora's book, the PMS outbox, or (write-back
   * gated) one "Do in <PMS>" task with every detail (FD-4).
   */
  bookVia: z.enum(['velora', 'pms', 'task']).optional(),
  lock: z.object({
    locked: z.boolean(),
    lockedThrough: LocalDate.nullable(),
    canOverride: z.boolean(),
  }),
  status: z.enum(['open', 'finished']),
  receiptDocumentId: Id.nullable(),
});
export type CheckoutSheet = z.infer<typeof CheckoutSheetSchema>;

export const CheckoutQuerySchema = z.object({
  /** Comma list of extra household appointment ids merged into the sheet. */
  with: z.string().optional(),
});

export const AddProcedureBodySchema = z.object({
  appointmentId: Id,
  code: z.string().min(2).max(10),
  tooth: z.string().max(4).nullable().optional(),
  surfaces: z.string().max(8).nullable().optional(),
  /** Set after the duplicate chip: keep both rows. */
  keepDuplicate: z.boolean().optional(),
});

/* ------------------------------------------------------------------ */
/* Retail at checkout (W15)                                            */
/* ------------------------------------------------------------------ */

/** A product the front desk can add to the visit with one click. */
export const RetailProductSchema = z.object({
  itemId: Id,
  name: z.string(),
  category: z.string().nullable(),
  priceCents: Cents,
  /** The code the sale posts under; null when the product has none yet. */
  pmsCode: z.string().nullable(),
  onHand: z.number().int(),
  /**
   * The code is in the practice's procedure codes. When false the product
   * still sells (QA r57): the "Post in <PMS>" task says to use the office's
   * retail code.
   */
  codeKnown: z.boolean(),
});
export type RetailProduct = z.infer<typeof RetailProductSchema>;

export const CheckoutRetailSaleSchema = z.object({
  id: Id,
  procedureId: Id,
  itemId: Id,
  itemName: z.string(),
  pmsCode: z.string(),
  priceCents: Cents,
  patientId: Id,
  soldAt: z.string(),
  soldByName: z.string(),
  /** Still removable: the line is not in the PMS and its day is not locked. */
  canRemove: z.boolean(),
});
export type CheckoutRetailSale = z.infer<typeof CheckoutRetailSaleSchema>;

/** `GET /v1/frontdesk/checkout/:appointmentId/retail`. */
export const CheckoutRetailSchema = z.object({
  products: z.array(RetailProductSchema),
  sales: z.array(CheckoutRetailSaleSchema),
  canSell: z.boolean(),
  /** How a sale reaches the PMS at this office ("Posted in Velora; a task ..."). */
  writeNote: z.string().nullable(),
});
export type CheckoutRetail = z.infer<typeof CheckoutRetailSchema>;

export const RetailSellBodySchema = z.object({
  itemId: Id,
  /** 1 by default; each unit is its own line. */
  quantity: z.number().int().min(1).max(10).optional(),
  /** Change the price for this sale (a discount); default the item's price. */
  priceCents: Cents.min(0).max(500_000).optional(),
});
export type RetailSellBody = z.input<typeof RetailSellBodySchema>;

export const RetailSellResultSchema = z.object({
  saleIds: z.array(Id),
  procedureIds: z.array(Id),
  taskIds: z.array(Id),
  /** The item's count on hand after the sale. */
  onHand: z.number().int(),
  writeBack: z.enum(['native', 'draft']),
});
export type RetailSellResult = z.infer<typeof RetailSellResultSchema>;

export const RetailRemoveBodySchema = z.object({
  reason: z.string().trim().max(300).nullable().optional(),
});

export const DuplicateDetailsSchema = z.object({
  duplicates: z.array(
    z.object({
      id: Id,
      code: z.string(),
      tooth: z.string().nullable(),
      surfaces: z.string().nullable(),
    }),
  ),
});

export const CreditBodySchema = z.object({ decision: z.enum(['applied', 'kept']) });

export const PAYMENT_TENDERS = ['cash', 'check', 'card', 'ach', 'financing', 'other'] as const;
export const PaymentBodySchema = z.object({
  /** Integer cents; never more than $5,000 in one checkout payment (WM-2). */
  amountCents: z.number().int().positive().max(500000),
  method: z.enum(PAYMENT_TENDERS),
  checkNumber: z.string().max(40).optional(),
  reference: z.string().max(100).optional(),
  /** Per user intent; a double click posts once. */
  idempotencyKey: z.string().regex(/^vel-[A-Za-z0-9_-]{8,190}$/),
  /** Required when posting into a closed (locked) day. */
  overrideReason: z.string().max(500).optional(),
  /** Household: appointment ids the payment covers (default: the sheet's). */
  appointmentIds: z.array(Id).optional(),
  /** Set after the user confirmed taking more than the family owes now (WM-2). */
  overpayment: z.boolean().optional(),
});
export type PaymentBody = z.infer<typeof PaymentBodySchema>;

export const PaymentResultSchema = z.object({
  paymentId: Id,
  replayed: z.boolean(),
  splits: z.array(z.object({ patientId: Id, cents: Cents })),
  writeBack: z.string().nullable(),
  /** `native`, `command` (queued to the PMS) or `manual` (a "Post in <PMS>" task). */
  writeBackMode: z.enum(['native', 'command', 'manual']).optional(),
  taskId: Id.nullable().optional(),
  pmsReference: z.string().nullable().optional(),
});

export const NextVisitBodySchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('book'),
    appointmentId: Id,
    startsAt: Instant,
    operatoryId: Id,
    providerId: Id,
    lengthMin: z.number().int().positive().multipleOf(5),
    /** The visit type (its pattern stretched to the length); default: the patient's recall type. */
    appointmentTypeId: Id.nullable().optional(),
    /** Reason for a booking the schedule rules allow only with one (a third concurrent X, SB-06). */
    override: z
      .object({ reason: z.string().trim().min(3).max(500) })
      .strict()
      .optional(),
  }),
  z.object({
    kind: z.literal('patient_will_call'),
    appointmentId: Id,
    reason: z.string().min(1).max(500),
  }),
]);

export const FinishBodySchema = z.object({
  skips: z.array(
    z.object({
      appointmentId: Id,
      key: ChecklistItemSchema.shape.key,
      reason: z.string().min(1),
      note: z.string().max(500).nullable().optional(),
    }),
  ),
});

export const FinishResultSchema = z.object({
  finished: z.array(Id),
  writeBack: z.string().nullable(),
});

export const WalkoutResultSchema = z.object({ documentId: Id, title: z.string() });

export const ComplianceReportSchema = z.object({
  from: LocalDate,
  to: LocalDate,
  offices: z.array(
    z.object({
      locationId: Id,
      shortName: z.string(),
      checkouts: z.number().int(),
      dueCents: Cents,
      collectedCents: Cents,
      /** Booked at checkout, including "Booked, pending <PMS>" (write-back gated). */
      reappointed: z.number().int(),
      /** Of `reappointed`, booked as a PMS task while write-back is off. */
      pendingPms: z.number().int().optional(),
      willCall: z.number().int(),
      medianCheckoutMinutes: z.number().nullable(),
    }),
  ),
  skipped: z.array(
    z.object({
      key: z.string(),
      reason: z.string(),
      userName: z.string(),
      count: z.number().int(),
    }),
  ),
});
export type ComplianceReport = z.infer<typeof ComplianceReportSchema>;

/* ------------------------------------------------------------------------ */
/* Close the Day                                                             */
/* ------------------------------------------------------------------------ */

export const ClosePaymentSchema = z.object({
  id: Id,
  method: z.string(),
  amountCents: Cents,
  payer: z.string(),
  patientId: Id.nullable(),
  paidAt: Instant,
  checkNumber: z.string().nullable(),
  reference: z.string().nullable(),
  inBag: z.boolean(),
  present: z.boolean(),
  origin: z.enum(['velora', 'pms']),
});
export type ClosePayment = z.infer<typeof ClosePaymentSchema>;

export const CloseChecklistItemSchema = z.object({
  key: z.enum(['visits', 'procedures', 'copays', 'unapplied', 'drawer', 'tomorrow']),
  label: z.string(),
  count: z.number().int(),
  done: z.boolean(),
  /**
   * `unavailable`: the PMS does not send what this item needs (no flow,
   * no visit link or no ledger on PulseSync v1), so Velora cannot tell;
   * shown as "Not available from <PMS>", never as a green tick (S3, S16).
   */
  availability: z.enum(['available', 'unavailable']).optional(),
  money: z.boolean(),
  detail: z.string(),
  rows: z.array(
    z.object({
      id: Id,
      label: z.string(),
      sub: z.string(),
      cents: Cents.nullable(),
      href: z.string().nullable(),
    }),
  ),
});
export type CloseChecklistItem = z.infer<typeof CloseChecklistItemSchema>;

export const DayTileSchema = z.object({
  date: LocalDate,
  state: z.enum(['closed', 'closed_late', 'open', 'today', 'no_activity', 'untracked']),
  label: z.string(),
});

export const DepositSummarySchema = z.object({
  id: Id,
  number: z.number().int(),
  status: z.enum(['issued', 'voided']),
  totalCents: Cents,
  itemCount: z.number().int(),
  issuedByName: z.string(),
  issuedAt: Instant,
  printCount: z.number().int(),
  voidReason: z.string().nullable(),
});
export type DepositSummary = z.infer<typeof DepositSummarySchema>;

export const ChangeAfterCloseSchema = z.object({
  id: Id,
  chip: z.enum(['Back-dated', 'Reversed', 'Edited after close']),
  closedDate: LocalDate,
  what: z.string(),
  byName: z.string(),
  at: Instant,
  amountCents: Cents,
});

export const CloseDraftSchema = z.object({
  presentPaymentIds: z.array(Id),
  countedCents: Cents.nullable(),
  varianceReason: z.string().nullable(),
  varianceNote: z.string().nullable(),
  notInDrawerReason: z.string().nullable(),
  approvedByName: z.string().nullable(),
  approvedById: Id.nullable(),
  /** Who last counted the bag (EO-03: they may not approve its variance). */
  countedByName: z.string().nullable().optional(),
  countedById: Id.nullable().optional(),
  version: z.number().int(),
});

export const CloseBlockerSchema = z.object({ code: z.string(), message: z.string() });

export const DayCloseViewSchema = z.object({
  id: Id,
  revision: z.number().int(),
  closedAt: Instant,
  closedByName: z.string(),
  countedCents: Cents,
  varianceCents: Cents,
  varianceReason: z.string().nullable(),
  approvedByName: z.string().nullable(),
  depositNumber: z.number().int().nullable(),
  hash: z.string(),
});

export const CloseDaySchema = z.object({
  office: OfficeModeSchema,
  date: LocalDate,
  today: LocalDate,
  /** `native`: Velora owns money and closes; `overlay`: read-only reconciliation. */
  mode: z.enum(['native', 'overlay']),
  status: z.enum(['open', 'ready', 'closed']),
  statusText: z.string(),
  closed: DayCloseViewSchema.nullable(),
  missedDays: z.array(z.object({ date: LocalDate, items: z.number().int() })),
  days: z.array(DayTileSchema),
  checklist: z.array(CloseChecklistItemSchema),
  payments: z.array(ClosePaymentSchema),
  draft: CloseDraftSchema,
  summary: z.object({
    expectedBagCents: Cents,
    selectedBagCents: Cents,
    countedCents: Cents.nullable(),
    varianceCents: Cents.nullable(),
    processorCents: Cents,
    totalCollectedCents: Cents,
    thresholdCents: Cents,
  }),
  blockers: z.array(CloseBlockerSchema),
  deposits: z.array(DepositSummarySchema),
  changesAfterClose: z.array(ChangeAfterCloseSchema),
  lockOnClose: z.boolean(),
  canClose: z.boolean(),
  /** Why this user cannot close (missing permission and who can), when `canClose` is false. */
  closeDeniedReason: z.string().nullable().optional(),
  canApprove: z.boolean(),
  /** Why this user cannot approve a variance that needs approval (EO-03). */
  approveDeniedReason: z.string().nullable().optional(),
  /** The approver, or anyone with close rights, may withdraw an approval. */
  canWithdrawApproval: z.boolean().optional(),
  currentUserId: Id,
  overlayNote: z.string().nullable(),
});
export type CloseDay = z.infer<typeof CloseDaySchema>;

export const CloseGridSchema = z.object({
  date: LocalDate,
  offices: z.array(
    z.object({
      office: OfficeModeSchema,
      mode: z.enum(['native', 'overlay']),
      status: z.enum(['open', 'ready', 'closed']),
      statusText: z.string(),
      bagCents: Cents,
      processorCents: Cents,
      openItems: z.number().int(),
      lateDays: z.number().int(),
      openDays: z.number().int(),
    }),
  ),
});
export type CloseGrid = z.infer<typeof CloseGridSchema>;

export const CloseQuerySchema = z.object({ date: LocalDate.optional() });

export const DraftPatchSchema = z.object({
  date: LocalDate,
  version: z.number().int(),
  presentPaymentIds: z.array(Id).optional(),
  countedCents: Cents.nonnegative().nullable().optional(),
  varianceReason: z.string().max(100).nullable().optional(),
  varianceNote: z.string().max(500).nullable().optional(),
  notInDrawerReason: z.string().max(500).nullable().optional(),
});

export const CheckNumberBodySchema = z.object({
  checkNumber: z.string().min(1).max(40),
  bank: z.string().max(80).optional(),
});

export const CloseActionBodySchema = z.object({ date: LocalDate, version: z.number().int() });
export const ReopenBodySchema = z.object({ date: LocalDate, reason: z.string().min(3).max(500) });
export const VoidDepositBodySchema = z.object({ reason: z.string().min(3).max(500) });
export const IssueDepositBodySchema = z.object({ date: LocalDate });

export const DaySheetSchema = z.object({
  closeId: Id,
  locationId: Id,
  officeName: z.string(),
  date: LocalDate,
  revision: z.number().int(),
  closedAt: Instant,
  closedByName: z.string(),
  hash: z.string(),
  prevHash: z.string().nullable(),
  production: z.object({ grossCents: Cents, adjustmentsCents: Cents, netCents: Cents }),
  collections: z.array(z.object({ method: z.string(), cents: Cents, count: z.number().int() })),
  byProvider: z.array(
    z.object({ provider: z.string(), productionCents: Cents, collectionsCents: Cents }),
  ),
  patientsSeen: z.number().int(),
  newPatients: z.number().int(),
  ar: z.object({
    beginningCents: Cents,
    chargesCents: Cents,
    collectionsCents: Cents,
    adjustmentsCents: Cents,
    transfersCents: Cents,
    endingCents: Cents,
    computedEndingCents: Cents,
    tiesOut: z.boolean(),
  }),
  netProductionRows: z.array(
    z.object({
      id: Id,
      kind: z.enum(['procedure', 'adjustment']),
      label: z.string(),
      patient: z.string(),
      cents: Cents,
    }),
  ),
  metricId: z.literal('prod.net.v1'),
  drawer: z.object({
    countedCents: Cents,
    expectedBagCents: Cents,
    selectedBagCents: Cents,
    varianceCents: Cents,
    varianceReason: z.string().nullable(),
    approvedByName: z.string().nullable(),
  }),
  depositNumber: z.number().int().nullable(),
  changesAfterClose: z.array(ChangeAfterCloseSchema),
});
export type DaySheet = z.infer<typeof DaySheetSchema>;

/* ------------------------------------------------------------------------ */
/* Tasks and contact log                                                     */
/* ------------------------------------------------------------------------ */

export const TASK_TEAM_VALUES = [
  'front_desk',
  'billing',
  'hygiene',
  'assistants',
  'doctors',
  'office_manager',
] as const;
export const TaskTeamSchema = z.enum(TASK_TEAM_VALUES);
export const TaskPrioritySchema = z.enum(['low', 'normal', 'high', 'urgent']);
export const TaskStatusSchema = z.enum([
  'open',
  'in_progress',
  'waiting_external',
  'done',
  'cancelled',
]);

export const TaskViewSchema = z.object({
  id: Id,
  locationId: Id,
  title: z.string(),
  detail: z.string().nullable(),
  patientId: Id.nullable(),
  patientName: z.string().nullable(),
  sourceKind: z.string().nullable(),
  sourceId: Id.nullable(),
  sourceLabel: z.string().nullable(),
  assigneeUserId: Id.nullable(),
  assigneeName: z.string().nullable(),
  team: TaskTeamSchema.nullable(),
  priority: TaskPrioritySchema,
  status: TaskStatusSchema,
  waitingOn: z.string().nullable(),
  dueAt: Instant.nullable(),
  slaMinutes: z.number().int().nullable(),
  slaRemainingMinutes: z.number().int().nullable(),
  overdue: z.boolean(),
  paused: z.boolean(),
  private: z.boolean(),
  escalated: z.boolean(),
  createdAt: Instant,
  createdByName: z.string().nullable(),
  completedAt: Instant.nullable(),
  outcomeCode: z.string().nullable(),
  outcomeNote: z.string().nullable(),
  version: z.number().int(),
  /** Routing category (voicemail, missed_call, lead, ...); null on tasks from before routing. */
  routingCategory: z.string().nullable().optional(),
});
export type TaskView = z.infer<typeof TaskViewSchema>;

export const TaskLaneSchema = z.enum([
  'due_today',
  'overdue',
  'upcoming',
  'waiting',
  'team',
  'mine',
  'done',
]);
export type TaskLane = z.infer<typeof TaskLaneSchema>;

export const TaskListQuerySchema = z.object({
  patientId: Id.optional(),
  q: z.string().max(100).optional(),
  includeDone: z.enum(['1', '0']).optional(),
});

export const TaskListSchema = z.object({
  tasks: z.array(TaskViewSchema),
  counts: z.record(z.string(), z.number().int()),
  now: Instant,
});
export type TaskList = z.infer<typeof TaskListSchema>;

export const CreateTaskBodySchema = z.object({
  title: z.string().min(1).max(200),
  detail: z.string().max(4000).nullable().optional(),
  patientId: Id.nullable().optional(),
  dueAt: Instant.nullable().optional(),
  team: TaskTeamSchema.nullable().optional(),
  assigneeUserId: Id.nullable().optional(),
  priority: TaskPrioritySchema.optional(),
  slaMinutes: z
    .number()
    .int()
    .positive()
    .max(60 * 24 * 60)
    .nullable()
    .optional(),
  private: z.boolean().optional(),
  sourceKind: z.string().max(40).nullable().optional(),
  sourceId: Id.nullable().optional(),
  dedupeKey: z.string().max(200).nullable().optional(),
});
export type CreateTaskBody = z.infer<typeof CreateTaskBodySchema>;

export const UpdateTaskBodySchema = z.object({
  version: z.number().int(),
  status: TaskStatusSchema.optional(),
  waitingOn: z.string().max(120).nullable().optional(),
  title: z.string().min(1).max(200).optional(),
  detail: z.string().max(4000).nullable().optional(),
  dueAt: Instant.nullable().optional(),
  team: TaskTeamSchema.nullable().optional(),
  assigneeUserId: Id.nullable().optional(),
  priority: TaskPrioritySchema.optional(),
  outcomeCode: z.string().max(60).nullable().optional(),
  outcomeNote: z.string().max(1000).nullable().optional(),
  accept: z.boolean().optional(),
});

export const AssigneeSchema = z.object({ id: Id, name: z.string(), roles: z.array(z.string()) });
export const AssigneeListSchema = z.object({
  users: z.array(AssigneeSchema),
  teams: z.array(z.object({ id: TaskTeamSchema, label: z.string() })),
});

export const ManagerViewSchema = z.object({
  teams: z.array(
    z.object({
      team: z.string(),
      label: z.string(),
      open: z.number().int(),
      overdue: z.number().int(),
      oldestOverdueMinutes: z.number().int().nullable(),
      avgCompletionMinutes: z.number().int().nullable(),
      privateOpen: z.number().int(),
    }),
  ),
});
export type ManagerView = z.infer<typeof ManagerViewSchema>;

/* ------------------------------------------------------------------------ */
/* Work queue (one prioritized queue, lanes per role)                        */
/* ------------------------------------------------------------------------ */

export const WORK_LANE_VALUES = ['calls', 'scheduling', 'insurance', 'posting', 'other'] as const;
export const WorkLaneSchema = z.enum(WORK_LANE_VALUES);
export const WorkRoleSchema = z.enum(['all', ...TASK_TEAM_VALUES]);
export type WorkRole = z.infer<typeof WorkRoleSchema>;

export const WorkQueueQuerySchema = z.object({
  role: WorkRoleSchema.optional(),
  /** '1': rebuild the queue's own items now instead of at most every few minutes. */
  refresh: z.enum(['1', '0']).optional(),
});

export const WorkQueueItemSchema = z.object({
  task: TaskViewSchema,
  lane: WorkLaneSchema,
  /** Higher first. */
  score: z.number().int(),
  /** Treatment or visit value (null without money.view or when unknown). */
  valueCents: z.number().int().nullable(),
  /** Insurance benefits left this plan year (null without money.view or when unknown). */
  benefitCents: z.number().int().nullable(),
  reason: z.string().nullable(),
  /** The visit (insurance) or the recall due date (scheduling). */
  at: Instant.nullable(),
});
export type WorkQueueItem = z.infer<typeof WorkQueueItemSchema>;

export const WorkQueueLaneSchema = z.object({
  lane: WorkLaneSchema,
  label: z.string(),
  role: TaskTeamSchema.nullable(),
  roleLabel: z.string(),
  slaMinutes: z.number().int().nullable(),
  description: z.string(),
  count: z.number().int(),
  overdue: z.number().int(),
  items: z.array(WorkQueueItemSchema),
});
export type WorkQueueLane = z.infer<typeof WorkQueueLaneSchema>;

export const WorkQueueSchema = z.object({
  role: WorkRoleSchema,
  lanes: z.array(WorkQueueLaneSchema),
  /** When the queue last rebuilt its own items (recall, treatment, eligibility). */
  syncedAt: Instant.nullable(),
  /** Sources that could not be read this time, in plain words. */
  notes: z.array(z.string()),
  now: Instant,
});
export type WorkQueue = z.infer<typeof WorkQueueSchema>;

export const WorkQueueSyncSchema = z.object({
  created: z.number().int(),
  closed: z.number().int(),
  notes: z.array(z.string()),
});
export type WorkQueueSync = z.infer<typeof WorkQueueSyncSchema>;

export const RecurrenceSchema = z.object({
  id: Id,
  title: z.string(),
  rule: z.string(),
  weekdays: z.array(z.number().int()),
  minute: z.number().int(),
  team: TaskTeamSchema.nullable(),
  priority: TaskPrioritySchema,
  active: z.boolean(),
});
export const RecurrenceListSchema = z.object({ recurrences: z.array(RecurrenceSchema) });
export const CreateRecurrenceBodySchema = z.object({
  title: z.string().min(1).max(200),
  weekdays: z.array(z.number().int().min(0).max(6)).min(1),
  minute: z.number().int().min(0).max(1439),
  team: TaskTeamSchema.nullable().optional(),
  priority: TaskPrioritySchema.optional(),
});

export const CONTACT_MODES = ['phone_out', 'phone_in', 'voicemail', 'in_person', 'note'] as const;
export const ContactModeSchema = z.enum(CONTACT_MODES);

export const TimelineEntrySchema = z.object({
  id: Id,
  channel: z.string(),
  mode: z.string().nullable(),
  direction: z.string(),
  body: z.string().nullable(),
  actorName: z.string().nullable(),
  actorKind: z.string(),
  occurredAt: Instant,
  status: z.string().nullable(),
  /** Created by a human in the contact log (editable source for tasks). */
  logged: z.boolean(),
  taskId: Id.nullable(),
  /**
   * Where the entry is kept: Velora's own contact log, or the PMS journal
   * (Dentrix's Office Journal and commlog, synced read-only). Absent means Velora.
   */
  origin: z.enum(['velora', 'pms']).optional(),
  /** "Dentrix"... for a PMS entry. */
  pmsName: z.string().nullable().optional(),
});
export type TimelineEntry = z.infer<typeof TimelineEntrySchema>;

export const ContactListSchema = z.object({ entries: z.array(TimelineEntrySchema) });

export const LogContactBodySchema = z.object({
  patientId: Id,
  mode: ContactModeSchema,
  body: z.string().min(1).max(4000),
  outcome: z.string().max(60).nullable().optional(),
});

/* ------------------------------------------------------------------------ */
/* Documents                                                                 */
/* ------------------------------------------------------------------------ */

export const DocumentKindSchema = z.enum([
  'insurance_card',
  'id',
  'eob',
  'referral_in',
  'referral_out',
  'consent',
  'medical_record',
  'lab_rx',
  'correspondence',
  'legal',
  'statement',
  'form_pdf',
  'photo',
  'other',
  /** A signed medical history, filed from forms (NEW-WF-07, X-09). */
  'health_history',
]);

export const DocumentRowSchema = z.object({
  id: Id,
  patientId: Id,
  title: z.string(),
  kind: DocumentKindSchema,
  docDate: LocalDate,
  mimeType: z.string(),
  sizeBytes: z.number().int(),
  pages: z.number().int(),
  version: z.number().int(),
  sensitivity: z.enum(['normal', 'restricted']),
  /** True when the viewer may not see a restricted document's content or title. */
  hidden: z.boolean(),
  signed: z.enum(['unsigned', 'signed', 'signed_other_version']),
  appointmentId: Id.nullable(),
  deletedAt: Instant.nullable(),
  restorable: z.boolean(),
  createdAt: Instant,
  createdByName: z.string().nullable(),
  receivedVia: z.string(),
  /** Office-configured document type (Document Center); null = filed under the base kind only. */
  typeId: Id.nullable().optional(),
  /** The type's name, when the viewer's offices can see the type. */
  typeName: z.string().nullable().optional(),
});
export type DocumentRow = z.infer<typeof DocumentRowSchema>;

export const DocumentListQuerySchema = z.object({
  patientId: Id,
  deleted: z.enum(['1', '0']).optional(),
});

export const DocumentListSchema = z.object({
  patientId: Id,
  patient: z.object({ name: z.string(), birthdate: LocalDate.nullable() }),
  documents: z.array(DocumentRowSchema),
  hold: z.object({ id: Id, reason: z.string(), placedAt: Instant }).nullable(),
  canAdmin: z.boolean(),
  canEdit: z.boolean(),
});
export type DocumentList = z.infer<typeof DocumentListSchema>;

export const DocumentDetailSchema = DocumentRowSchema.extend({
  originalFilename: z.string(),
  note: z.string().nullable(),
  tags: z.array(z.string()),
  versions: z.array(
    z.object({
      version: z.number().int(),
      sha256: z.string(),
      rotation: z.number().int(),
      createdAt: Instant,
      changeNote: z.string().nullable(),
    }),
  ),
  signature: z.discriminatedUnion('state', [
    z.object({ state: z.literal('unsigned') }),
    z.object({ state: z.literal('signed'), signerName: z.string(), signedAt: Instant }),
    z.object({
      state: z.literal('signed_other_version'),
      signedVersion: z.number().int(),
      signerName: z.string(),
      signedAt: Instant,
    }),
  ]),
  links: z.array(z.object({ kind: z.string(), id: Id, label: z.string() })),
  rotation: z.number().int(),
});
export type DocumentDetail = z.infer<typeof DocumentDetailSchema>;

export const UploadQuerySchema = z.object({
  patientId: Id,
  filename: z.string().min(1).max(500),
  kind: DocumentKindSchema.optional(),
  title: z.string().max(200).optional(),
  docDate: LocalDate.optional(),
  /** File under an office document type; its base kind becomes the document's kind. */
  typeId: Id.optional(),
});

export const UpdateDocumentBodySchema = z.object({
  title: z.string().min(1).max(200).optional(),
  kind: DocumentKindSchema.optional(),
  /** An office document type (sets kind to its base kind), or null to clear it. */
  typeId: Id.nullable().optional(),
  docDate: LocalDate.optional(),
  note: z.string().max(4000).nullable().optional(),
  tags: z.array(z.string().min(1).max(40)).max(10).optional(),
  sensitivity: z.enum(['normal', 'restricted']).optional(),
});

export const MoveDocumentBodySchema = z.object({
  toPatientId: Id,
  reason: z.string().min(1).max(100),
  note: z.string().max(500).nullable().optional(),
});
export const MoveResultSchema = z.object({ moved: z.boolean(), privacyTaskId: Id.nullable() });

export const RotateBodySchema = z.object({
  degrees: z.union([z.literal(90), z.literal(180), z.literal(270)]),
});
export const SignBodySchema = z.object({
  signerName: z.string().min(1).max(120),
  signerKind: z.enum(['patient', 'guardian', 'provider', 'staff']),
});

export const HoldBodySchema = z.object({ patientId: Id, reason: z.string().min(3).max(500) });
export const RetentionResultSchema = z.object({
  examined: z.number().int(),
  purged: z.number().int(),
  held: z.number().int(),
  log: z.array(z.object({ documentId: Id, decision: z.enum(['purge', 'held']) })),
});
export type RetentionResult = z.infer<typeof RetentionResultSchema>;

export const OkSchema = z.object({ ok: z.literal(true) });

/* ------------------------------------------------------------------------ */
/* Document types (office-configurable)                                      */
/* ------------------------------------------------------------------------ */

export const DocumentTypeSchema = z.object({
  id: Id,
  /** null: a practice-wide type every office sees. */
  locationId: Id.nullable(),
  name: z.string(),
  baseKind: DocumentKindSchema,
  sortOrder: z.number().int(),
  archived: z.boolean(),
  /** Documents filed under this type (all offices the viewer can see). */
  documents: z.number().int(),
});
export type DocumentType = z.infer<typeof DocumentTypeSchema>;

export const DocumentTypeListQuerySchema = z.object({ archived: z.enum(['1', '0']).optional() });
export const DocumentTypeListSchema = z.object({
  types: z.array(DocumentTypeSchema),
  canManage: z.boolean(),
});
export type DocumentTypeList = z.infer<typeof DocumentTypeListSchema>;

export const CreateDocumentTypeBodySchema = z.object({
  name: z.string().trim().min(1).max(60),
  baseKind: DocumentKindSchema,
  sortOrder: z.number().int().min(0).max(10_000).optional(),
  /** Practice-wide instead of this office only. */
  practiceWide: z.boolean().optional(),
});
export const UpdateDocumentTypeBodySchema = z.object({
  name: z.string().trim().min(1).max(60).optional(),
  baseKind: DocumentKindSchema.optional(),
  sortOrder: z.number().int().min(0).max(10_000).optional(),
  archived: z.boolean().optional(),
});

/* ------------------------------------------------------------------------ */
/* Task routing by position and My tasks (owner request 2026-10-28)          */
/* ------------------------------------------------------------------------ */

/** Mirrors @velora/domain/frontdesk TASK_ROUTING_CATEGORIES. */
export const TASK_ROUTING_CATEGORY_VALUES = [
  'voicemail',
  'missed_call',
  'text_reply',
  'lead',
  'insurance_verification',
  'unscheduled_treatment',
  'recall',
  'billing',
  'forms',
  'other',
] as const;
export const TaskRoutingCategorySchema = z.enum(TASK_ROUTING_CATEGORY_VALUES);
export type TaskRoutingCategoryKey = z.infer<typeof TaskRoutingCategorySchema>;

export const TaskRoutingRuleSchema = z.object({
  id: Id,
  /** null: every office (an office's own rule wins). */
  locationId: Id.nullable(),
  category: TaskRoutingCategorySchema,
  team: TaskTeamSchema.nullable(),
  userId: Id.nullable(),
  userName: z.string().nullable(),
});
export type TaskRoutingRule = z.infer<typeof TaskRoutingRuleSchema>;

export const TaskRoutingPersonSchema = z.object({
  id: Id,
  name: z.string(),
  roles: z.array(z.string()),
  locationIds: z.array(Id),
  /** Positions set by hand; empty: the role's default teams apply. */
  positions: z.array(z.object({ team: TaskTeamSchema, locationId: Id.nullable() })),
  /** Teams from the person's roles (used when no positions are set). */
  roleTeams: z.array(TaskTeamSchema),
});
export type TaskRoutingPerson = z.infer<typeof TaskRoutingPersonSchema>;

export const TaskRoutingSchema = z.object({
  categories: z.array(
    z.object({
      key: TaskRoutingCategorySchema,
      label: z.string(),
      description: z.string(),
      recommendedTeam: TaskTeamSchema,
    }),
  ),
  rules: z.array(TaskRoutingRuleSchema),
  teams: z.array(z.object({ id: TaskTeamSchema, label: z.string() })),
  people: z.array(TaskRoutingPersonSchema),
  /** Open tasks per category with nobody and no team on them, in the offices in scope. */
  unrouted: z.record(z.string(), z.number().int()),
  canManage: z.boolean(),
});
export type TaskRouting = z.infer<typeof TaskRoutingSchema>;

export const SaveTaskRoutingBodySchema = z.object({
  /** null: the practice-wide rules (every office). */
  locationId: Id.nullable(),
  /** The full set for that scope: categories left out have no rule there. */
  rules: z
    .array(
      z.object({
        category: TaskRoutingCategorySchema,
        team: TaskTeamSchema.nullable(),
        userId: Id.nullable(),
      }),
    )
    .max(TASK_ROUTING_CATEGORY_VALUES.length),
  /** Also route open tasks that have no team and nobody on them yet. */
  applyToOpen: z.boolean().default(false),
});
export type SaveTaskRoutingBody = z.infer<typeof SaveTaskRoutingBodySchema>;

export const SaveTaskRoutingResultSchema = z.object({
  saved: z.number().int(),
  rerouted: z.number().int(),
});

export const SetPositionsBodySchema = z.object({
  userId: Id,
  /** null: in every office the person works. */
  locationId: Id.nullable(),
  /** Empty: back to the role's default teams. */
  teams: z.array(TaskTeamSchema).max(TASK_TEAM_VALUES.length),
});
export type SetPositionsBody = z.infer<typeof SetPositionsBodySchema>;

export const ReassignTasksBodySchema = z
  .object({
    taskIds: z.array(Id).min(1).max(200),
    /** Both null: back to unassigned (the routing rules no longer apply). */
    team: TaskTeamSchema.nullable(),
    assigneeUserId: Id.nullable(),
  })
  .strict();
export type ReassignTasksBody = z.infer<typeof ReassignTasksBodySchema>;

export const ReassignTasksResultSchema = z.object({
  updated: z.number().int(),
  skipped: z.number().int(),
});

export const MyTasksQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(25).optional(),
});

export const MyTaskItemSchema = TaskViewSchema.extend({
  category: TaskRoutingCategorySchema,
  categoryLabel: z.string(),
  /** Assigned to me by name (true) or to one of my positions (false). */
  assignedToMe: z.boolean(),
});
export type MyTaskItem = z.infer<typeof MyTaskItemSchema>;

export const MyTasksSchema = z.object({
  /** The short list, most important first (at most `limit`, default 10). */
  items: z.array(MyTaskItemSchema),
  /** Everything on my list right now (shown and not shown). */
  total: z.number().int(),
  /** Tasks I finished today (office day). */
  doneToday: z.number().int(),
  /** The positions the list was built from. */
  teams: z.array(TaskTeamSchema),
  now: Instant,
});
export type MyTasks = z.infer<typeof MyTasksSchema>;
